import * as React from "react";
import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";

import { useNavigate } from "react-router-dom";

import {
  useGetRcsTemplateListQuery,
  useDeleteRcsTemplateMutation,
  useGetRcsTemplateViewQuery,
} from "../store/services/rcstemplateApi";
import { layoutCSS } from "../component/common/commonStyle";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";

// Shared border style for table cells
const tableCellBorder = {
  borderBottom: "0.5px solid #1b1b1bff",
  borderRight: "0.5px solid #1b1b1bff",
};

export default function RcsTemplateList() {
  // =======================
  // STATES
  // =======================
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [currentTime, setCurrentTime] = useState("");

  // =======================
  // API HOOKS
  // =======================
  const {
    data: getRcsTemplateList,
    refetch,
    isFetching,
    isError,
  } = useGetRcsTemplateListQuery(
    {
      id: customerId,
      page: page + 1,
      limit: 10,
    },
    {
      skip: !customerId,
    }
  );

  const { data } = useGetRcsTemplateViewQuery(selectedTemplateId);
  const [deleteRcsTemplate] = useDeleteRcsTemplateMutation();

  const navigate = useNavigate();

  const rowsPerPage = 10;

  React.useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found in local storage");
        return;
      }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) {
        toast.error("Customer ID not found in decoded token");
      } else {
        setCustomerId(id);
        console.log("customerId (decoded):", id);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Helper: detect video URLs by extension
  const isVideoUrl = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  const getCardWidth = (cardWidth) => {
    switch (cardWidth) {
      case "Small":
        return "50%";
      default:
        return "95%";
    }
  };

  const handleNext = (cardCount) => {
    setSelectedCardIndex((prev) => (prev + 1) % cardCount);
  };

  const handlePrev = (cardCount) => {
    setSelectedCardIndex((prev) => (prev - 1 + cardCount) % cardCount);
  };

  // Filter rows based on search term (client-side filtering)
  const filteredRows = useMemo(() => {
    if (!getRcsTemplateList?.data) return [];

    if (!searchTerm.trim()) return getRcsTemplateList.data;

    const term = searchTerm.toLowerCase().trim();
    return getRcsTemplateList.data.filter((row) =>
      row.template_name?.toLowerCase().includes(term)
    );
  }, [getRcsTemplateList?.data, searchTerm]);

  const getButtonTooltip = (btn) => {
    const details = [];
    const actionId = Number(btn.button_action_id);

    // ID 2: URL Action
    if (actionId === 2) {
      if (btn.url) details.push(`URL: ${btn.url}`);
      if (btn.url_action_application) details.push(`App URL: ${btn.url_action_application}`);
    }
    // ID 3: Dialer Action
    else if (actionId === 3) {
      if (btn.dialer_action_phone_number) details.push(`Phone: ${btn.dialer_action_phone_number}`);
    }
    // ID 4: Location Action
    else if (actionId === 4) {
      if (btn.location_query) details.push(`Location: ${btn.location_query}`);
      if (btn.latitude && btn.longitude) details.push(`Coords: ${btn.latitude}, ${btn.longitude}`);
    }
    // ID 5: Calendar Action
    else if (actionId === 5) {
      if (btn.calendar_title) details.push(`Event: ${btn.calendar_title}`);
      if (btn.calendar_description) details.push(`Desc: ${btn.calendar_description}`);
      if (btn.calendar_start_date_time) details.push(`Start: ${btn.calendar_start_date_time.split(/[T ]/)[0]}`);
      if (btn.calendar_end_date_time) details.push(`End: ${btn.calendar_end_date_time.split(/[T ]/)[0]}`);
    }

    return details.length > 0 ? details.join(" | ") : "";
  };

  // Reset to first page when search term changes
  React.useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // =======================
  // HANDLERS
  // =======================
  const handleTemplateView = (id) => {
    setSelectedTemplateId(id);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedTemplateId(null);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      await deleteRcsTemplate(selectedTemplateId).unwrap();
      await refetch();
      handleClose();
      setSelectedTemplateId(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // =======================
  // RENDER
  // =======================
  return (
    <Box sx={layoutCSS}>
      {/* HEADER */}
      <Box fullwidth>
        <Typography variant="h6" sx={{ mb: 2 }}>
          RCS Template
        </Typography>
      </Box>

      {/* ACTION BAR */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
          p: 1,
        }}
      >
        <TextField
          placeholder="Search by template name"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            width: 220,
            fontSize: "0.8rem",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "6px 8px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#1e88e5" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            fontSize: 13,
            borderRadius: 1.5,
            px: 2,
            py: 0.5,
          }}
          onClick={() => navigate("/rcs-template")}
        >
          Create Template
        </Button>
      </Box>

      {/* Search results info */}
      {searchTerm && (
        <Box sx={{ mb: 1, px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Found {filteredRows.length} template
            {filteredRows.length !== 1 ? "s" : ""} matching "{searchTerm}"
          </Typography>
        </Box>
      )}

      {/* TABLE */}
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          mb: 2,
          mt: 2,
        }}
      >
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 800 }} aria-label="simple table">
            <TableHead
              sx={{
                bgcolor: "#0288d1",
                "& th": {
                  color: "white",
                  fontSize: "0.8rem",
                  padding: "6px",
                  borderBottom: "0.5px solid #1b1b1bff",
                  borderRight: "0.5px solid #1b1b1bff",
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Template Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Template Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Agent
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Updated On
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      py: 3,
                      ...tableCellBorder,
                    }}
                  >
                    <Typography color="text.secondary">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRows.length > 0 ? (
                filteredRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        ...tableCellBorder,
                      }}
                    >
                      <Tooltip
                        title={row.template_name}
                        componentsProps={{
                          tooltip: { sx: { fontSize: ".79rem" } },
                        }}
                      >
                        <span>{row.template_name}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tableCellBorder}>{row.rcs_template_type}</TableCell>
                    <TableCell align="center" sx={tableCellBorder}>
                      {row.status}
                    </TableCell>
                    <TableCell align="center" sx={tableCellBorder}>
                      {row.agent}
                    </TableCell>
                    <TableCell align="center" sx={tableCellBorder}>
                      {row.createdat ? (
                        <>
                          {dayjs(row.createdat).format("DD/MM/YYYY")}
                          <br />
                          {dayjs(row.createdat).format("HH:mm")}
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center" sx={tableCellBorder}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        useFlexGap
                      >
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleTemplateView(row.template_id)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            handleClickOpen();
                            setSelectedTemplateId(row.rcs_template_id);
                          }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      py: 3,
                      ...tableCellBorder,
                    }}
                  >
                    {searchTerm ? (
                      <Typography color="text.secondary">
                        No templates found matching "{searchTerm}"
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">
                        No templates available
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={getRcsTemplateList?.totalRecords || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      </Box>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"Are you sure?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this template?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDeleteTemplate} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.5)",
          },
        }}
      >
        {/* phone wrapper */}
        <DialogContent sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "flex-start", pt: 6, backgroundColor: "transparent" }}>
          {/* phone card */}
          <Box
            sx={{
              width: "300px",
              minHeight: "400px",
              maxHeight: "75vh",
              backgroundColor: "#fff",
              border: "8px solid #000",
              borderRadius: "32px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            {/* status bar */}
            <Box
              sx={{
                width: "100%",
                height: "30px",
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem" }}>{currentTime}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <WifiIcon sx={{ fontSize: "16px" }} />
                <NetworkCellIcon sx={{ fontSize: "16px" }} />
                <BatteryFullIcon sx={{ fontSize: "16px" }} />
              </Stack>
            </Box>

            {/* close button */}
            <Button
              onClick={handleViewClose}
              sx={{
                position: "absolute",
                top: 20,
                right: 10,
                minWidth: 0,
                padding: 0.5,
                zIndex: 10,
                color: "#000",
              }}
            >
              <ClearIcon fontSize="small" />
            </Button>

            {/* main content area */}
            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flexGrow: 1,
                backgroundColor: "#f5f5f5",
                minHeight: "300px",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#ccc",
                  borderRadius: "2px",
                  "&:hover": {
                    bgcolor: "#999",
                  },
                },
              }}
            >
              {data?.data ? (
                <Stack spacing={2}>
                  {/* === Template Header === */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: "0.95rem",
                        color: "#000",
                      }}
                    >
                      {data.data.templateDetails?.template_name}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#f0f0f0",
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {data.data.templateDetails?.rcs_template_type}
                    </Box>
                  </Box>

                  {/* === Content Card === */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    {/* === SWITCH by Type === */}
                    {(() => {
                      const type = data.data.templateDetails?.rcs_template_type;

                      switch (type) {
                        case "Text":
                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#333",
                                lineHeight: 1.6,
                                fontSize: "0.9rem",
                              }}
                            >
                              {data.data.templateDetails?.rcs_text}
                            </Typography>
                          );

                        case "Rich Card":
                          return (
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  backgroundColor: "#f0f0f0",
                                }}
                              >
                                {isVideoUrl(data.data.templateDetails?.rich_card_image) ? (
                                  <video
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: "8px",
                                      display: "block",
                                    }}
                                  >
                                    <source
                                      src={data.data.templateDetails?.rich_card_image}
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                  </video>
                                ) : (
                                  <img
                                    src={data.data.templateDetails?.rich_card_image}
                                    alt="Rich Card"
                                    style={{
                                      width: "100%",
                                      borderRadius: "8px",
                                      display: "block",
                                    }}
                                  />
                                )}
                              </Box>

                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}
                                >
                                  {data.data.templateDetails?.rich_card_title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                                  {data.data.templateDetails?.rich_card_description}
                                </Typography>
                              </Box>
                            </Stack>
                          );

                        case "Carousel":
                          const cards = data.data.carousel_cards || [];
                          const selectedCard = cards[selectedCardIndex] || null;

                          return (
                            <Stack spacing={1.5}>
                              {/* Selector Buttons */}
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                {cards.map((_, idx) => (
                                  <Button
                                    key={idx}
                                    onClick={() => setSelectedCardIndex(idx)}
                                    sx={{
                                      minWidth: "32px",
                                      height: "32px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      transition: "all 0.3s ease",
                                      ...(selectedCardIndex === idx
                                        ? {
                                          backgroundColor: "#000",
                                          color: "#fff",
                                        }
                                        : {
                                          backgroundColor: "#e0e0e0",
                                          color: "#333",
                                          "&:hover": {
                                            backgroundColor: "#ccc",
                                          },
                                        }),
                                    }}
                                  >
                                    {idx + 1}
                                  </Button>
                                ))}
                              </Box>

                              {/* Selected Card */}
                              {selectedCard ? (
                                <Stack spacing={1}>
                                  <Box
                                    sx={{
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {isVideoUrl(selectedCard.carousel_image) ? (
                                      <video
                                        controls
                                        style={{
                                          width: "100%",
                                          borderRadius: "8px",
                                          display: "block",
                                        }}
                                      >
                                        <source
                                          src={selectedCard.carousel_image}
                                          type="video/mp4"
                                        />
                                        Your browser does not support the video tag.
                                      </video>
                                    ) : (
                                      <img
                                        src={selectedCard.carousel_image}
                                        alt={`Card ${selectedCard.card_no}`}
                                        style={{
                                          width: "100%",
                                          borderRadius: "8px",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}>
                                      {selectedCard.carousel_title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                                      {selectedCard.carousel_description}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography color="text.secondary">No card data available.</Typography>
                              )}
                            </Stack>
                          );

                        default:
                          return (
                            <Typography color="text.secondary">Unknown type</Typography>
                          );
                      }
                    })()}
                  </Box>

                  {/* === Buttons Card === */}
                  {data.data.buttonInformation?.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        p: 2,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Suggested Replies */}
                        {data.data.buttonInformation.filter((btn) => Number(btn.button_action_id) === 1).length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                              Suggested Replies
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                              {data.data.buttonInformation
                                .filter((btn) => Number(btn.button_action_id) === 1)
                                .map((btn, idx) => {
                                  const tooltipTitle = getButtonTooltip(btn);
                                  return (
                                    <Tooltip key={`action1-${idx}`} title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          borderRadius: "6px",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          border: "1px solid #ccc",
                                          color: "#333",
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            backgroundColor: "#f0f0f0",
                                            borderColor: "#999",
                                          },
                                        }}
                                      >
                                        {btn.suggestion_text}
                                      </Button>
                                    </Tooltip>
                                  );
                                })}
                            </Stack>
                          </Box>
                        )}

                        {/* Suggested Actions */}
                        {data.data.buttonInformation.filter((btn) => Number(btn.button_action_id) !== 1).length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                              Suggested Actions
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                              {data.data.buttonInformation
                                .filter((btn) => Number(btn.button_action_id) !== 1)
                                .map((btn, idx) => {
                                  const tooltipTitle = getButtonTooltip(btn);
                                  return (
                                    <Tooltip key={`other-${idx}`} title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          borderRadius: "6px",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          backgroundColor: "#000",
                                          color: "#fff",
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            backgroundColor: "#333",
                                          },
                                        }}
                                      >
                                        {btn.suggestion_text}
                                      </Button>
                                    </Tooltip>
                                  );
                                })}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography>Loading...</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}